import { type LensCardView, type LensDataset, type LensDatasetField } from '~/types/leLens';

import { AGGREGATES } from './buildPlan';
import { prepareFields } from './datasetFields';
import { parseGrain } from './lensGrain';

const DATASET_COMMENT = /^\s*--\s*dataset:\s*(.+?)\s*$/im;
const VIEW_COMMENT = /^\s*--\s*view:\s*(.+?)\s*$/im;
const FIELD_COMMENT = /^\s*--\s*field:\s*(.+?)\s*$/im;
const EXTEND_COMMENT = /^\s*--\s*extend:\s*(.+?)\s*$/im;

export type CardDatasetBinding = { datasetId: string; view: LensCardView };

/** A card answered by arithmetic over columns the dataset already returns. */
export type CardFieldBinding = CardDatasetBinding & { field: LensDatasetField };

/** A card that names a dataset to extend, still needing the server-side
 *  extend-check guard before it is safe to apply: whether the rewrite keeps
 *  every existing column, at what cost, and which dependent cards would
 *  change is what that endpoint answers, not this parser. */
export type CardExtension = { datasetId: string; sql: string; view: LensCardView };

/**
 * Reads an answer that reused a dataset instead of writing a query.
 *
 * The binding is checked against the dataset it names rather than taken on
 * trust: a card bound to a column the dataset does not return renders nothing,
 * and a card bound to the wrong dataset renders a number that looks right. Both
 * fail closed here, and the caller writes SQL instead.
 */
export function extractDatasetBinding(
  answer: string,
  datasets: ReadonlyArray<{ id: string; columns: ReadonlyArray<string> }>,
): CardDatasetBinding | undefined {
  const named = answer.match(DATASET_COMMENT)?.[1]?.trim();
  const dataset = datasets.find((entry) => entry.id === named);
  if (!dataset) {
    return undefined;
  }

  const view = parseViewLine(answer.match(VIEW_COMMENT)?.[1] ?? '');
  if (!view || !viewResolvesAgainst(view, dataset.columns)) {
    return undefined;
  }

  return { datasetId: dataset.id, view };
}

/** The declared columns a view names, unwrapping a grain expression
 *  ("month(on_date)") to the column underneath it: only x is ever
 *  materialised as a grain, so the same name anywhere else is read back as a
 *  literal key no row has, rendering the measure null. */
function referencedViewColumns(view: LensCardView): Array<string> {
  return [view.x, view.series, view.value, view.ratio?.of, view.ratio?.per].filter((column): column is string =>
    Boolean(column),
  );
}

/** The first name in `named` that `columns` does not carry, matched
 *  case-insensitively and grain-aware: a model's or a dataset's casing need
 *  not agree, and a grain wrapper resolves on the column inside it. */
function firstUnresolvedColumn(named: ReadonlyArray<string>, columns: ReadonlyArray<string>): string | undefined {
  const known = new Set(columns.map((column) => column.toLowerCase()));
  return named.find((column) => !known.has((parseGrain(column)?.column ?? column).toLowerCase()));
}

/** Only x is ever materialised as a grain: found on series or value instead,
 *  the column it wraps is reported as the reason, since reading it there
 *  would draw a literal key no row has rather than the bucket the answer
 *  meant. */
function misplacedGrainColumn(view: LensCardView): string | undefined {
  return parseGrain(view.series)?.column ?? parseGrain(view.value)?.column;
}

/**
 * The first column a view names that these declared columns do not carry, or
 * undefined when every one resolves. A grain named anywhere but x is reported
 * by the column it wraps, since that is the one thing actually missing: the
 * position, not the name.
 *
 * Shared by `viewResolvesAgainst` and `unresolvableViewColumn`, which differ
 * only in whether the caller wants a yes/no or the name to explain it with.
 */
export function missingViewColumn(view: LensCardView, columns: ReadonlyArray<string>): string | undefined {
  return misplacedGrainColumn(view) ?? firstUnresolvedColumn(referencedViewColumns(view), columns);
}

/**
 * Whether a cut can be drawn from a result set carrying exactly these columns.
 *
 * Checked case-insensitively, since the model's casing and a dataset's
 * declared casing need not agree, and a casing mismatch failing silently used
 * to fall through to an unnecessary duplicate own-SQL card.
 *
 * Shared with the combine path, which repoints existing cards onto a shared
 * query and has the same two ways to go quietly wrong: a cut naming a column
 * the query does not return draws nothing, and a cut naming the wrong column
 * draws a number that looks right.
 */
export function viewResolvesAgainst(view: LensCardView, columns: ReadonlyArray<string>): boolean {
  if (!referencedViewColumns(view).length) {
    return false;
  }
  return missingViewColumn(view, columns) === undefined;
}

/** `x=day; series=brand; value=revenue_aud; aggregate=sum` */
function parseViewLine(line: string): LensCardView | undefined {
  const view: LensCardView = {};
  for (const part of line.split(';')) {
    const [rawKey, ...rest] = part.split('=');
    const key = rawKey.trim().toLowerCase();
    const value = rest.join('=').trim();
    if (!value) {
      continue;
    }
    if (key === 'x' || key === 'series' || key === 'value') {
      view[key] = value;
    }
    if (key === 'ratio') {
      // "gross_margin_aud/ttv_aud". Both halves must be present; one alone is
      // not a ratio, and taking it as the value would render a total where the
      // card asked for a rate.
      const [of, per] = value.split('/').map((half) => half.trim());
      if (of && per) {
        view.ratio = { of, per };
      }
    }
    if (key === 'aggregate' && (AGGREGATES as ReadonlyArray<string>).includes(value)) {
      view.aggregate = value as LensCardView['aggregate'];
    }
  }
  return Object.keys(view).length ? view : undefined;
}

/**
 * Reads an answer that added a calculated field to a dataset rather than
 * writing a query.
 *
 * `-- field: margin_rate = gross_margin_aud / ttv_aud on totals`
 *
 * The field is checked against the dataset's real columns and its formula is
 * parsed here, so a field that cannot compute never reaches the spec: kept, it
 * would render the card empty, which reads as "no data" rather than as "this
 * formula is wrong".
 */
export function extractFieldBinding(
  answer: string,
  datasets: ReadonlyArray<{ id: string; columns: ReadonlyArray<string> }>,
): CardFieldBinding | undefined {
  const named = answer.match(DATASET_COMMENT)?.[1]?.trim();
  const dataset = datasets.find((entry) => entry.id === named);
  const declared = answer.match(FIELD_COMMENT)?.[1]?.trim();
  if (!dataset || !declared) {
    return undefined;
  }

  const field = parseFieldLine(declared);
  if (!field) {
    return undefined;
  }
  // A field naming a column the dataset does not return, or one that shadows a
  // column it does, is refused rather than saved.
  const prepared = prepareFields([field], [...dataset.columns]);
  if (!prepared.row.length && !prepared.aggregate.length) {
    return undefined;
  }

  const view = parseViewLine(answer.match(VIEW_COMMENT)?.[1] ?? '');
  // The field is the measure whether or not the answer said so: it is the
  // reason this rung was taken.
  const resolved: LensCardView = { ...(view ?? {}), value: field.name };
  const referenced = [resolved.x, resolved.series].filter((column): column is string => Boolean(column));
  if (referenced.some((column) => !dataset.columns.includes(parseGrain(column)?.column ?? column))) {
    return undefined;
  }

  return { datasetId: dataset.id, field, view: resolved };
}

/** `margin_rate = gross_margin_aud / ttv_aud on totals` */
function parseFieldLine(line: string): LensDatasetField | undefined {
  const at = line.indexOf('=');
  if (at < 1) {
    return undefined;
  }
  const name = line.slice(0, at).trim();
  let expression = line.slice(at + 1).trim();
  // The suffix is how the answer says which side of the aggregation the field
  // lives on. Absent, it is a rate more often than not, and a rate computed per
  // row is the error this whole mechanism exists to prevent.
  let when: LensDatasetField['when'] = 'aggregate';
  const suffix = /\s+on\s+(totals|each\s+row)\s*$/i.exec(expression);
  if (suffix) {
    when = /each/i.test(suffix[1]) ? 'row' : 'aggregate';
    expression = expression.slice(0, suffix.index).trim();
  }

  return name && expression && /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? { name, expression, when } : undefined;
}

/**
 * Reads an answer that named a dataset to extend rather than writing a fresh
 * query.
 *
 * This used to also verify, by regexing the candidate SQL's projection, that
 * the rewrite kept every existing column and genuinely added one. That check
 * is gone: it could only prove a name absent from a text guess, never confirm
 * one was really there, and the same question now has a real answer, the
 * dry-run-backed extend-check endpoint, which runs the candidate once and
 * diffs every dependent card's actual result. This function's job shrinks to
 * finding the candidate for that endpoint to judge: which dataset, which of
 * the answer's SQL blocks is the rewrite, and what view the new card wants.
 */
export function extractDatasetExtension(
  answer: string,
  sqlBlocks: ReadonlyArray<string>,
  datasets: ReadonlyArray<{ id: string }>,
): CardExtension | undefined {
  const named = answer.match(EXTEND_COMMENT)?.[1]?.trim();
  if (!named || !datasets.some((entry) => entry.id === named)) {
    return undefined;
  }

  const view = parseViewLine(answer.match(VIEW_COMMENT)?.[1] ?? '');
  const [sql] = sqlBlocks;
  if (!view?.value || !sql) {
    return undefined;
  }

  return { datasetId: named, sql, view };
}

/**
 * Every column a view could resolve against for this dataset: its declared
 * schema from a BigQuery dry run, its calculated fields, and its legacy
 * declared dimensions, kept for a dataset that named its dimensions before
 * this schema existed. Never a guess read out of the dataset's SQL text: a
 * dataset with none of these declares nothing, which is the "not yet
 * declared" state a caller renders as a prompt to run one, not a reason to
 * fabricate a list that can be silently wrong.
 */
export function datasetKnownColumns(dataset: LensDataset): Set<string> {
  const known = new Set<string>();
  for (const column of dataset.columns ?? []) {
    known.add(column.name);
  }
  for (const field of dataset.fields ?? []) {
    known.add(field.name);
  }
  for (const dimension of dataset.dimensions ?? []) {
    known.add(dimension);
  }
  return known;
}

/**
 * The plain sentence to show instead of saving a card that can never resolve.
 *
 * An answer-derived card can name a measure the question implied rather than
 * one the dataset returns, an average or a rate most of all. Saved, it renders
 * as a dead tile on every load with no way back except editing the spec. A
 * where entry naming a column the dataset does not return fails the same way,
 * silently: the filter matches nothing rather than refusing to save.
 */
export function unresolvableViewColumn(view: LensCardView, dataset: LensDataset): string | undefined {
  const named = [
    ...referencedViewColumns(view),
    ...(view.values ?? []),
    ...(view.where ?? []).map((entry) => entry.column),
  ];
  const missing = misplacedGrainColumn(view) ?? firstUnresolvedColumn(named, [...datasetKnownColumns(dataset)]);
  if (!missing) {
    return undefined;
  }
  return `This card asks for ${missing}, which ${dataset.name} does not return. Pick a column the dataset has, or add ${missing} to the dataset as a calculated field first.`;
}
